import { Storage, API } from 'aws-amplify';
import { getPromotionsTable } from '../../graphql/queries';
import {
    updatePromotionsTable,
    createCurrencyAllocationRulesTable,
    updateCurrencyCurrencyTable,
    createPrizeCatalogueTable
} from '../../graphql/mutations';
import { Formik } from 'formik';
import uniqid from "uniqid";
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import moment from 'moment-timezone';
import { Box, Typography } from '@mui/material';
import { Notification } from '../../common/Notification/Notification';
import { assignPromotionValidationSchema } from '../AssignPromotion/AssignPromotionValidationSchema';
import { AssignPromotionForm } from './AssignPromotionForm';
import { NotFoundPage } from '../NotFound/NotFoundPage';
import { ICreatePromotionState, campaignData } from '../../types/componentTypes/assignPromo';
import { CurrencyTableItem } from '../../types/componentTypes/CurrencyTypes';
import { boxTypeMapNames } from './../EditPromotion/FriendReferral/constants/referralRewards-constants'
import { removeFalsyValuesFromCurrency } from '../Currencies/helpers/currency-helpers';
import { newCurrencyAssignment } from '../EditPromotion/CurrenciesTab/services/api-calls';
import { defaultNotifState } from '../../constants/currency-constants';
import { getCurrenciesData } from '../../utils/currenciesUtils';
import { generateRsConfigParams } from '../../utils/pluginStoreUtils'

import './AssignPromotionPage.css';
import '../../App.css';

Storage.configure({
    customPrefix: {
        public: '',
        protected: '',
        private: '',
    },
});

const startDateTimestamp = moment().add(5, 'minutes');
const validationSchema = assignPromotionValidationSchema();

const setDefaultTags = (inputTempParams) => {
    let tags: string[] = []
    switch (inputTempParams.flowOption) {
    case 'Instant Win':
        if(inputTempParams?.burnPincodes) {
            tags.push('instantwinpe')
        }
        if (inputTempParams?.useCurrency) {
            tags.push('instantwincost')
        }
        break
    case 'Collect & Get':
        return['C&G PE']
    case 'Prize Draw (Lottery)':
        return['prizedrawlottery']
    case 'Auto Collect & Get':
        return['autocng']
    default:
        console.log(`No supported mechanic tags`);
    }
    return tags;
}

const initialValues: ICreatePromotionState = {
    promotionId: '',
    tempParams: {
        codeType: [''],
        currencyNames: [],
        flowOption: '',
        useCurrency: false,
        availableCurrencies: [{ country: '', name: '', currency_id: '' }],
        burnPincodes: false,
        luckyNumbersBatchAmount: 0,
        luckyNumbersBatchSize: 0,
        pincodeOriginValitidy: false,
        listPrizes: false,
        redeemPrize: false,
        addTransaction: false,
        queryVouchers: false,
        redeemVoucher: false,
        queryWallet: false,
        autoRedeemCnG: false,
        currencyValidity: false,
        additionalInformation: false,
        addRules: true,
        addCaptcha: false,
        addCaptchaSecret: false, // temp until secret option is removed
        validityDays: 1,
        startDateTime: moment().format('YYYY-MM-DD HH:mm'),
        endDateTime: moment().add(14, 'days').format('YYYY-MM-DD HH:mm'),
        limits: {
            minAge: false,
            calendarDay: false,
            rolling: false,
            lifetime: false,
            useRollingCustomDuration: false,
        },
    },
    configurationParameters: {
        language: '',
        country: '',
        currencies: [],
        configurationStartUtc: moment().format('YYYY-MM-DD HH:mm'),
        configurationEndUtc: moment().add(14, 'days').format('YYYY-MM-DD HH:mm'),
        configurationDatesTimezone: '',
        configurationSubMechanic: '',
        startEndDateCheck: true,
        validity: {},
        additionalInformation: { name: '', tags: [] },
        userIdType: 'cds',
    },
    configurationId: '',
    flow: '',
    params: {
        algorithm: 'winningMoments',
        alwaysWin: '',
        reduceAmount: [{ currencyId: '', amount: '' }],
        lotIds: [''],
        campaignIds: [''],
        imageEntry: false
    },
    secrets: {
        mixCodesParameters: [{ programId: '', secret: '', uri: '' }],
    },
    checkerLambdas: [],
    flowLambdas: [],
    participationLimits: {
        participationLimit: 1,
        participationLimitTime: 1,
        participationLimitTimeZone: '',
        participationLimitCalendarDatesRange: {
            startDate: parseInt(moment().format('x')),
            endDate: parseInt(moment().add(1, 'days').format('x')),
        },
        participationLimitMultipleChecks: false,
        participationLifetimeLimit: 1,
        participationLimitStartEndDatesRange: {
            startDate: parseInt(moment().format('x')),
            endDate: parseInt(moment().add(1, 'days').format('x')),
        },
        participationLimitCalendarDatesLimit: 1,
        participationLimitCalendarDatesTimeZone: '',
        minAge: 12,
        participationCalendarCycleLimit: {
            limit: 1,
            period: 'week',
            firstDayOfWeek: 'monday'
        },
    },
    allocationRules: [
        {
            programId: '',
            lotId: '',
            currencyId: '',
            currencyName: '',
            amount: null,
            validityCheck: false,
            validity: null,
        },
    ],
    additionalDetails: {},
    priorityOrder: 'ASC',
    proofType: 'none',
    maxParticipationIds: 10,
    useStatusReserved: false,
    ...generateRsConfigParams(''),
};

const initialCampaign: campaignData = {
    configurations: [],
    promotion_id: '',
    promotion_name: '',
    promotion_author: '',
    promotion_owner: '',
    digital_experience: [],
    promotion_market: [],
    promotion_brand: [],
    promotion_start_utc: new Date(),
    promotion_end_utc: new Date(),
    last_modified: undefined,
};

export type IUpdateCurrenciesWithPromotionData = {
    campId: string;
    promoId: string;
    availableCurrencies: CurrencyTableItem[];
    selectedCurrencyIds: string[];
};

export const isReceiptScannerWithAutoRedeemCnG = (promoFlow, config) => {
  return (
    promoFlow === 'autoRedeemCnG' &&
    config?.proofType === 'receiptScanner'
  );
};

/**
 * AB#2787177
 * When using RS and the proofType is `proofOfPurchase`,
 * the `pincodeToCurrency` and `transactionLambda` flow lambdas are not required.
 *
 * The `pluginResponseOnly` flag must also be removed; otherwise,
 * the core logic (`autoRedeemPrizeLambda`) will not be executed.
 *
 * @param configData
 *
 * @param lambdasToRemove
 * @returns configData
 */
export const getReformatedАutoRedeemCnAndRsConfig = (configData, lambdasToRemove = ['pincodeToCurrency', 'transactionLambda']) => {
  const promoFlow = 'autoRedeemCnG';

  delete configData?.pluginParams?.pluginResponseOnly;

  const flowLambdas = configData?.flow?.[promoFlow]?.flowLambdas;

  if (Array.isArray(flowLambdas)) {
    configData.flow[promoFlow].flowLambdas = flowLambdas.filter(
      lambda => !lambdasToRemove.includes(lambda)
    );
  }

  return configData;
}

export const updateCurrenciesWithPromotionData = async ({
    campId,
    promoId,
    selectedCurrencyIds,
    availableCurrencies,
}: IUpdateCurrenciesWithPromotionData) => {
    if (!selectedCurrencyIds) return;

    return availableCurrencies.reduce(async (acc: Promise<{}>, currValue: CurrencyTableItem) => {
        const { currency_id, currency_assignment } = currValue;

        if (!selectedCurrencyIds.includes(currency_id)) return acc;

        if (!currency_assignment) {
            await newCurrencyAssignment({ promoId, campId, currValue });
            return acc;
        }

        const parsedCurrAssignment: Record<string, string[]> = JSON.parse(currency_assignment);
        const itemsForUpdating = (parsedCurrAssignment && parsedCurrAssignment[campId]) ? [...parsedCurrAssignment[campId]] : [];

        const currIndex = selectedCurrencyIds.indexOf(currency_id);
        if (!itemsForUpdating.includes(selectedCurrencyIds[currIndex])) itemsForUpdating.push(promoId);

        const updatedCurrency = {
            ...removeFalsyValuesFromCurrency(currValue),
            currency_assignment: JSON.stringify({
                ...parsedCurrAssignment,
                [campId]: itemsForUpdating,
            }),
        };

        await API.graphql({ query: updateCurrencyCurrencyTable, variables: { input: updatedCurrency } });

        return acc;
    }, Promise.resolve({}));
};

export const ensureOrderOfFlowLambdas = (inputFlowLambdas: string[]) => {
    const orderOfLambdas = [ "burnPincodes", "currencyReducer","promoEntryLambda","instantWin", "luckyNumbersLambda", "pincodeToCurrency","transactionLambda", "autoRedeemPrizeLambda"]
    let newFlowLambdas: string[] = []
    inputFlowLambdas.forEach(value => {
        newFlowLambdas[orderOfLambdas.indexOf(value)] = value
    })
    return newFlowLambdas.filter(value => value)
}

export const retrieveCampaignData = async (promotionId) => {
    try {
        const queryResult: any = await API.graphql({
            query: getPromotionsTable,
            variables: { promotion_id: promotionId },
        });
        const campaignData = queryResult.data.getPromotionsTable;
        return campaignData;
    } catch (err) {
        console.error('Failed to fetch campaign data:', err);
    }
};

function AssignPromotionPage() {
    const { promotionId } = useParams();
    const [notificationState, setNotificationState] = useState(defaultNotifState);
    const [initialState, setInitialState] = useState(initialValues);
    const [campaignData, setCampaignData] = useState(initialCampaign);
    const [campaignMarkets, setCampaignMarkets] = useState([]);
    const [createPromoDisabled, setCreatePromoDisabled] = useState(false);
    const [step, setStep] = useState(0);
    const [disabled, setDisabled] = useState(false);
    const updateStep = (num: number) => {
        const newStep = step + num;
        setStep(newStep);
    };

    useEffect(() => {
        let updatedState: ICreatePromotionState;
        if (!promotionId) {
            setDisabled(true);
        } else {
            retrieveCampaignData(promotionId)
                .then((data) => {
                    const campaignHasStarted: boolean = moment(data.promotion_start_utc) < moment()
                    const campaignIsInactive: boolean = moment(data.promotion_end_utc) < moment() || data.archived
                    if (campaignIsInactive) {
                        setDisabled(true);
                        setCreatePromoDisabled(true)
                        return;
                    }
                    const startDate = (campaignHasStarted ? startDateTimestamp : moment(data.promotion_start_utc)).format('YYYY-MM-DD HH:mm');
                    const endDate = moment(data.promotion_end_utc).format('YYYY-MM-DD HH:mm');

                    updatedState = {
                        ...initialValues,
                        promotionId,
                        tempParams: {
                            ...initialValues.tempParams,
                            startDateTime: startDate,
                            endDateTime: endDate,
                        },
                        configurationParameters: {
                            ...initialValues.configurationParameters,
                            configurationStartUtc: startDate,
                            configurationEndUtc: endDate,
                        },
                        participationLimits: {
                            ...initialValues.participationLimits,
                            participationLimitCalendarDatesRange: {
                                startDate,
                                endDate,
                            },
                            participationLimitStartEndDatesRange: {
                                startDate,
                                endDate,
                            },
                        },
                    };
                    setCampaignData(data);
                    setCampaignMarkets(data.promotion_market);
                })
                .then(() => {
                    getCurrenciesData()
                        .then((data) => {
                            const activeCurrencies = data.filter((item) => item.is_active === null || item.is_active );
                            updatedState = {
                                ...updatedState,
                                tempParams: {
                                    ...updatedState.tempParams,
                                    availableCurrencies: activeCurrencies || [{ country: '', name: '', currency_id: '' }],
                                },
                            };
                            setInitialState(updatedState);
                        })
                        .catch((e) => {
                            console.error('Failed to fetch currencies:', e);
                        });
                })
                .catch((e) => {
                    setNotificationState({
                        open: true,
                        title: 'Campaign not found',
                        content: 'Supplied campaign id does not exist! Please go back to campaigns list.',
                        level: 'error',
                    });
                    console.error('Retrieval of campaign metadata failed with:', e);
                    setDisabled(true);
                });
        }
    }, [promotionId]);

    const clearObject = (values: { [key: string]: any }) => {
        Object.keys(values).forEach((key) => {
            if (key === "amount") {
                values[key] = values[key] ?? "";
            }

            if (values[key] && typeof values[key] === 'object') {
                clearObject(values[key]);
            }

            if (
                (Array.isArray(values[key]) && values[key].length === 0) ||
                (typeof values[key] === 'object' && Object.keys(values[key]).length === 0) ||
                values[key] === '' ||
                values[key] === null
            ) {
                delete values[key];
            }
        });

        return values;
    };

    const groupBySku = (rules: Array<any>) => {
    return rules.reduce((acc, rule) => {
        if (!rule.SKU) return acc;
        if (!acc[rule.SKU]) {
            acc[rule.SKU] = [];
        }
        acc[rule.SKU].push({
            currencyId: rule.currencyId,
            amount: rule.amount
        });
        return acc;
    }, {} as Record<string, Array<{ currencyId: string; amount: number }>>);
}

    const validateFlowLambdas = ({ promoFlow, proofType, flowLambdas }: { promoFlow:string; proofType: string; flowLambdas: string[] }) => {
        const uniqueFlowLambdas = new Set(flowLambdas);
        if (proofType === 'mixCodes') {
            uniqueFlowLambdas.add('burnPincodes');
        }
        if (promoFlow === 'autoRedeemCnG') {
            uniqueFlowLambdas.add('pincodeToCurrency');
        }
        return ensureOrderOfFlowLambdas([...uniqueFlowLambdas]);
    };

    const createLuckyNumberBatches = async (startDate, endDate, inputNumberOfBatches, inputAmountPerBatch, inputConfigurationId) => {
        const promises = [];
for (let i = 0; i < inputNumberOfBatches; i++) {
  const batchObject = {
    configuration_id: inputConfigurationId,
    prize_id: uniqid(),
    start_date: startDate,
    priority: i + 1,
    end_date: endDate,
    total_amount:  inputAmountPerBatch,
    total_available: inputAmountPerBatch,
    voucher_dist: 'false'
};
  promises.push( API.graphql({ query: createPrizeCatalogueTable, variables: { input: {...batchObject} } }));
}

await Promise.all(promises);
    }

    const restructureValues = (values: ICreatePromotionState) => {
        const {
            params,
            participationLimits,
            secrets,
            flowLambdas,
            checkerLambdas,
            flow,
            tempParams,
            additionalDetails,
            allocationRules,
            ...rest
        } = values;
        let validityValues;
        if (!values.tempParams.pincodeOriginValitidy) {
            delete params.lotIds;
            delete params.campaignIds;
        }

        if (!values.tempParams.useCurrency) {
            delete params.reduceAmount;
        }


        if (values.flow === 'luckyNumbers') {
            delete tempParams?.luckyNumbersBatchSize;
            delete tempParams?.luckyNumbersBatchAmount;
        }

        if (values.proofType !== 'receiptScanner') {
            delete rest.pluginParams;
            delete rest.uploadImage;
            delete rest.pluginRoutes;

        }

        if (rest?.referralRewards) {
            Object.keys(boxTypeMapNames).forEach(boxName => {
                if (rest?.referralRewards[boxName]) {
                    rest.configurationParameters?.currencies.includes(rest.referralRewards[boxName]?.currency) ||
                    rest.configurationParameters?.currencies.push(rest.referralRewards[boxName]?.currency)
                }
            })
        }

        if (values.tempParams.useCurrency && flow === 'promoEntry') {
            delete params.algorithm;
        }

        const flowParams = (() => {
            if (['instantWin', 'promoEntry', 'luckyNumbers'].includes(flow)) {
                return { params: { ...clearObject({ ...params, ...participationLimits }) } };
            }
            if (['redeemPincodeForCurrencies', 'autoRedeemCnG'].includes(flow)) {
                delete params.algorithm
                delete params.imageEntry
                return { params: clearObject(params) };
            }
            return {};
        })();

        const temp = {
            flow: {
                [flow]: {
                    ...flowParams,
                    ...(values.proofType === 'mixCodes' && { secrets }),
                    flowLambdas: validateFlowLambdas({ promoFlow: flow, proofType: values.proofType, flowLambdas }),
                    ...(checkerLambdas.length > 0 && { checkerLambdas: [...new Set(checkerLambdas)] }),
                    ...(rest.pluginRoutes && { pluginRoutes: rest.pluginRoutes })
                },
                ...additionalDetails,
                ...(rest.uploadImage && { uploadImage: rest.uploadImage })
            },
        };
        delete rest.uploadImage;
        if (tempParams.currencyValidity) {
            validityValues = rest.configurationParameters.currencies.reduce((acc: {[key:string]: number}, currency: string) => {
                acc[currency] = tempParams.validityDays;
                return acc;
            }, {});
        } else {
            delete rest.configurationParameters.validity;
        }

        const configData = {
            ...rest,
            configurationParameters: {
                ...rest.configurationParameters,
                configurationStartUtc: tempParams.startDateTime,
                configurationEndUtc: tempParams.endDateTime,
                ...(validityValues && { validity: validityValues }),
            },
            ...temp,
            ...(rest.pluginParams && { pluginParams: rest.pluginParams })
        };
        delete configData.pluginRoutes
    if (values.useStatusReserved === true) {
        //@ts-ignore
        configData.flow.instantWin.params.useStatusReserved = true;
        //@ts-ignore
        configData.flow.acceptReservedVoucher.params.maxParticipationIds = values.maxParticipationIds
    }
        ['priorityOrder', 'maxParticipationIds', 'useStatusReserved'].forEach((key) => delete configData[key]);
        configData.configurationParameters.additionalInformation.tags = setDefaultTags(tempParams);
        if ( configData.flow[flow]?.params?.minAge ) {
            delete configData.configurationParameters.additionalInformation.minAge;
        }
        if (values.proofType === 'receiptScanner' && allocationRules) {
            configData.pluginParams.rsAllocationMap = groupBySku(allocationRules)
            configData.flow[flow].flowLambdas = configData.flow[flow].flowLambdas.filter(lambda => lambda !== 'burnPincodes')
            configData.flow[flow].checkerLambdas = configData.flow[flow].checkerLambdas?.filter(lambda => lambda !== "pincodeOriginValidityCheckerLambda")
            if (configData.flow[flow].checkerLambdas?.length === 0) {
                delete configData.flow[flow].checkerLambdas
            }
            configData.pluginParams.pluginResponseOnly =  true
        }
        if (isReceiptScannerWithAutoRedeemCnG(flow, configData)) {
           return clearObject(getReformatedАutoRedeemCnAndRsConfig(configData, ['pincodeToCurrency', 'transactionLambda']));
        }
        return clearObject(configData);
    };

    const concatenateColumnValues = (...column: any) => `${column.join('|')}`;

    const createAllocationRule = async (configurationId: string, rule: { [key: string]: any }, ruleId: string) => {
        const insertParams = {
            amount: rule.amount,
            configuration_id: configurationId,
            currency_id: rule.currencyId,
            lot_id: rule.lotId,
            program_id: rule.programId,
            validity: rule.validity,
            rule_id: ruleId,
            rule_active: true,
        };

        await API.graphql({ query: createCurrencyAllocationRulesTable, variables: { input: insertParams } });
    };

    return (
        <>
            <Notification notificationState={notificationState} setNotificationState={setNotificationState} />
            {disabled && <NotFoundPage titleText={createPromoDisabled && 'Promotions can be added only to active campaigns'}/>}
            {!disabled && (
                <Box className='assign-promotion-wrap'>
                    <Typography variant='h4' gutterBottom className='main'>
                        Add promotion
                    </Typography>
                    <Box>
                        <Formik
                            initialValues={initialState}
                            enableReinitialize={true}
                            validationSchema={validationSchema}
                            onSubmit={async (values, actions) => {
                                try {
                                    if (values.tempParams.addRules && values.proofType === 'mixCodes') {
                                        for (const rule of values.allocationRules) {
                                            const ruleId = concatenateColumnValues(
                                                rule.programId,
                                                rule.lotId,
                                                rule.currencyId
                                            );
                                            await createAllocationRule(values.configurationId, rule, ruleId);
                                        }
                                    }
                                    if (values.flow === 'luckyNumbers') {
                                        await createLuckyNumberBatches(
                                            values.tempParams.startDateTime,
                                            values.tempParams.endDateTime,
                                            values.tempParams.luckyNumbersBatchAmount,
                                            values.tempParams.luckyNumbersBatchSize,
                                            values.configurationId)
                                    }

                                    const result = restructureValues(values);

                                    const fileName = `${result.configurationId}/conf.txt`;
                                    await Storage.put(fileName, JSON.stringify(result), { contentType: 'text/plain' });
                                    const updatedCampaignData = {
                                        ...campaignData,
                                        configurations:
                                            campaignData.configurations !== null
                                                ? [...campaignData.configurations, result.configurationId]
                                                : [result.configurationId],
                                        last_modified: moment().toDate().getTime(),
                                    };
                                    await API.graphql({
                                        query: updatePromotionsTable,
                                        variables: { input: updatedCampaignData },
                                    });

                                    await updateCurrenciesWithPromotionData({
                                        campId: values.promotionId,
                                        promoId: values.configurationId,
                                        availableCurrencies: values.tempParams.availableCurrencies,
                                        selectedCurrencyIds: result?.configurationParameters?.currencies,
                                    });

                                    updateStep(1);
                                } catch (e) {
                                    actions.resetForm();
                                    setStep(0);
                                }
                            }}>
                            {(formik) => (
                                <form onSubmit={formik.submitForm}>
                                    <AssignPromotionForm
                                        step={step}
                                        updateStep={updateStep}
                                        campaignMarkets={campaignMarkets}
                                    />
                                </form>
                            )}
                        </Formik>
                    </Box>
                </Box>
            )}
        </>
    );
}

export { AssignPromotionPage };
